import { useEffect, useRef, useMemo, useState } from 'react'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'
import { useBookStore } from '../../stores'
import StoryParticles from '../effects/Particles'
import Lighting from '../effects/Lighting'
import StoryText from '../text/StoryText'
import SceneRenderer from './SceneRenderer'
import PaperBook from './PaperBook'

// ---- WebGL 可用性检测 ----
function isWebGLAvailable(): boolean {
  try {
    const canvas = document.createElement('canvas')
    return !!(
      window.WebGLRenderingContext &&
      (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
    )
  } catch {
    return false
  }
}

/**
 * Book 阅读器
 * - 从 URL ?mode=reader&book={slug} 读取绘本
 * - 通过 API 加载书籍（含 scene_3d 场景 JSON）
 * - 渲染：3D 场景 + 灯光 + 粒子 + 故事文本 + 交互
 * - WebGL 不可用时降级为 2D 文本展示
 */
export default function Book() {
  const groupRef = useRef<THREE.Group>(null)
  const { currentPage, currentBook, bookLoading, bookError, fetchBook, setCurrentPage } = useBookStore()
  const [webglAvailable] = useState(() => isWebGLAvailable())
  const prevBookId = useRef<string | null>(null)

  // 从 URL 读取 book slug，若无则默认加载 "勇敢的小刺猬"
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const slug = params.get('book')
    if (slug) {
      fetchBook(slug)
    } else {
      fetchBook('brave-little-hedgehog')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 同步 totalPages；首次加载书籍时从 URL page 参数设置初始页码
  useEffect(() => {
    if (currentBook?.pages?.length) {
      const total = currentBook.pages.length
      if (currentBook.slug !== prevBookId.current) {
        prevBookId.current = currentBook.slug
        const params = new URLSearchParams(window.location.search)
        const pageParam = params.get('page')
        if (pageParam) {
          const targetPage = Math.max(0, Math.min(parseInt(pageParam, 10) - 1, total - 1))
          setCurrentPage(targetPage)
          return
        }
      }
      setCurrentPage(Math.min(currentPage, total - 1))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBook])

  const pageData = useMemo(() => {
    if (!currentBook?.pages?.length) return null
    return currentBook.pages[Math.min(currentPage, currentBook.pages.length - 1)]
  }, [currentBook, currentPage])

  // ---- 加载中状态 ----
  if (bookLoading && !currentBook) {
    return (
      <group>
        {/* 加载提示背景板 */}
        <mesh position={[0, 0, 0.5]}>
          <planeGeometry args={[5, 2.5]} />
          <meshBasicMaterial color="#1a1a2e" />
        </mesh>
        <mesh position={[0, 0.3, 0.6]}>
          <planeGeometry args={[4, 0.8]} />
          <meshBasicMaterial color="#2d2d44" />
        </mesh>
        {/* 旋转加载指示器 */}
        <LoadingSpinner position={[0, 0.3, 0.7]} />
      </group>
    )
  }

  // ---- 加载失败 ----
  if (bookError && !currentBook) {
    return (
      <group>
        <mesh position={[0, 0, 0.5]}>
          <planeGeometry args={[5, 2.5]} />
          <meshBasicMaterial color="#1a1a2e" />
        </mesh>
        <mesh position={[0, 0.3, 0.6]}>
          <planeGeometry args={[4, 0.8]} />
          <meshBasicMaterial color="#4a1a1a" />
        </mesh>
      </group>
    )
  }

  // ---- 无数据 ----
  if (!currentBook || !pageData) {
    return (
      <group>
        <mesh position={[0, 0, 0.5]}>
          <planeGeometry args={[5, 2.5]} />
          <meshBasicMaterial color="#1a1a2e" />
        </mesh>
      </group>
    )
  }

  const scene = pageData.scene_3d || {}
  const elements = Array.isArray(scene.elements) ? scene.elements : []
  const lighting = scene.lighting || 'moonlight'
  const particle = scene.particle || null
  const interactions = Array.isArray(pageData.interactions) ? pageData.interactions : []
  const content = pageData.content || ''

  return (
    <group ref={groupRef} position={[0, 0, 0]}>
      {/* 场景层：元素渲染在当前页幕布后方（背景/环境） */}
      <group position={[0, 0, -0.2]}>
        {webglAvailable ? (
          <SceneRenderer elements={elements} />
        ) : (
          <Fallback2DContent elements={elements} />
        )}
      </group>

      {/* 环境效果 */}
      <Lighting preset={lighting} />
      {particle && <StoryParticles preset={particle} />}

      {/* 简单可点击交互物（从 interactions JSON 渲染，置于纸张前方） */}
      {interactions.length > 0 && webglAvailable && (
        <group position={[0, 0, 1.2]}>
          <InteractiveLayer interactions={interactions} elements={elements} />
        </group>
      )}

      {/* 物理纸页层：paperlab 纸张承载故事内容（核心顶级效果） */}
      {webglAvailable && (
        <PaperBook
          index={currentPage}
          text={content}
          title={pageData.title}
        />
      )}

      {/* 故事文本：纸张上方提供朗读高亮（TTS 词级高亮） */}
      <StoryText
        text={content}
        position={[0, -2.9, 0.4]}
        audioUrl={pageData.audio_url}
        timestamps={pageData.timestamps}
      />

      {/* 页眉提示（书名/页码） */}
      <mesh position={[0, 3.2, 0.2]}>
        <planeGeometry args={[6, 0.8]} />
        <meshBasicMaterial color="#000000" transparent opacity={0.25} />
      </mesh>
    </group>
  )
}

// ---- 旋转加载指示器 ----
function LoadingSpinner({ position }: { position: [number, number, number] }) {
  const meshRef = useRef<THREE.Mesh>(null)
  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.z = state.clock.elapsedTime * 2
    }
  })
  return (
    <mesh ref={meshRef} position={position}>
      <ringGeometry args={[0.3, 0.4, 16]} />
      <meshBasicMaterial color="#4ade80" transparent opacity={0.8} />
    </mesh>
  )
}

// ---- WebGL 不可用时降级为 2D 文本展示 ----
function Fallback2DContent({ elements }: { elements: any[] }) {
  return (
    <group>
      {/* 背景板 */}
      <mesh position={[0, 0, 0]}>
        <planeGeometry args={[8, 5]} />
        <meshBasicMaterial color="#2d2d44" />
      </mesh>
      {/* 文本内容区域 */}
      <mesh position={[0, 1, 0.1]}>
        <planeGeometry args={[6, 2]} />
        <meshBasicMaterial color="#1a1a2e" />
      </mesh>
      {/* 简单的元素标记 */}
      {elements.map((el, i) => (
        <mesh key={i} position={el.position || [0, 0, 0]}>
          <sphereGeometry args={[0.2, 8, 8]} />
          <meshBasicMaterial color={i % 2 === 0 ? '#4ade80' : '#60a5fa'} />
        </mesh>
      ))}
    </group>
  )
}

// ---- 交互层：将 interactions JSON 映射为可点击的 3D 标记 ----
function InteractiveLayer({
  interactions,
  elements,
}: {
  interactions: any[]
  elements: any[]
}) {
  const { recordInteraction } = useBookStore()
  const refs = useRef<Map<string, THREE.Mesh>>(new Map())

  // 由 interaction.target 定位元素位置
  const targets = useMemo(() => {
    const map = new Map<string, [number, number, number]>()
    elements.forEach((el) => {
      if (el.position) map.set(el.id, el.position)
    })
    return map
  }, [elements])

  return (
    <group>
      {interactions.map((it, i) => {
        const pos = targets.get(it.target) || [i * 0.8 - 1, 0.5, 0.2]
        return (
          <mesh
            key={i}
            position={pos as [number, number, number]}
            ref={(m) => { if (m) refs.current.set(String(i), m) }}
            onClick={(e) => {
              e.stopPropagation()
              recordInteraction({ elementId: it.target || String(i), type: it.action || 'click', timestamp: Date.now() })
            }}
          >
            <sphereGeometry args={[0.2, 12, 12]} />
            <meshBasicMaterial color="#FFD54F" transparent opacity={0.7} />
          </mesh>
        )
      })}
    </group>
  )
}

